import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store";
import {
  getPharmacyLicenses,
  createPharmacyLicense,
  updatePharmacyLicense,
  deletePharmacyLicense,
  setActivePharmacyLicense,
} from "../services/pharmacyLicenseService";
import {
  PharmacyLicenseResponse,
  PharmacyLicenseCreateRequest,
  PharmacyLicenseUpdateRequest,
} from "../types/pharmacyLicense";
import { selectMarketplaceDetailed } from "../store/auth-slice";
import { fetchMarketplaceInfoDetailed } from "../store/auth-actions";

interface UsePharmacyLicensesReturn {
  licenses: PharmacyLicenseResponse[];
  loading: boolean;
  error: string | null;
  activeLicenseId: number | null;
  refetch: () => Promise<void>;
  create: (
    data: PharmacyLicenseCreateRequest,
    files: File[]
  ) => Promise<boolean>;
  update: (
    data: PharmacyLicenseUpdateRequest,
    files: File[]
  ) => Promise<boolean>;
  remove: (licenseId: number) => Promise<boolean>;
  setActive: (licenseId: number | null) => Promise<boolean>;
}

export const usePharmacyLicenses = (): UsePharmacyLicensesReturn => {
  const dispatch = useDispatch();
  const [licenses, setLicenses] = useState<PharmacyLicenseResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get marketplace info from Redux (for IDs)
  const marketplaceInfo = useSelector(
    (state: RootState) => state.auth.marketplaceInfo
  );

  // Get detailed marketplace info from Redux (for active license)
  const marketplaceInfoDetailed = useSelector(selectMarketplaceDetailed);

  // Active license comes from Redux
  const activeLicenseId =
    marketplaceInfoDetailed?.marketplacePharmacyLicenseResponse?.id ?? null;

  const fetchLicenses = useCallback(async () => {
    if (
      !marketplaceInfo?.branchMarketplaceId ||
      !marketplaceInfo?.marketplaceId
    ) {
      console.warn("Cannot fetch licenses: marketplace info not available");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getPharmacyLicenses(
        marketplaceInfo.branchMarketplaceId,
        marketplaceInfo.marketplaceId
      );
      setLicenses(data);
    } catch (err) {
      console.error("Failed to fetch licenses:", err);
      setError("Failed to load pharmacist licenses");
    } finally {
      setLoading(false);
    }
  }, [marketplaceInfo?.branchMarketplaceId, marketplaceInfo?.marketplaceId]);

  // Fetch on mount and when marketplace info changes
  useEffect(() => {
    fetchLicenses();
  }, [fetchLicenses]);

  const create = async (
    data: PharmacyLicenseCreateRequest,
    files: File[]
  ): Promise<boolean> => {
    try {
      await createPharmacyLicense(data, files);
      await fetchLicenses(); // Refresh list
      return true;
    } catch (err) {
      console.error("Failed to create license:", err);
      setError("Failed to create pharmacist license");
      return false;
    }
  };

  const update = async (
    data: PharmacyLicenseUpdateRequest,
    files: File[]
  ): Promise<boolean> => {
    try {
      await updatePharmacyLicense(data, files);
      await fetchLicenses(); // Refresh list
      return true;
    } catch (err) {
      console.error("Failed to update license:", err);
      setError("Failed to update pharmacist license");
      return false;
    }
  };

  const remove = async (licenseId: number): Promise<boolean> => {
    try {
      await deletePharmacyLicense(licenseId);
      await fetchLicenses(); // Refresh list
      return true;
    } catch (err) {
      console.error("Failed to delete license:", err);
      setError("Failed to delete pharmacist license");
      return false;
    }
  };

  /**
   * Set the active pharmacist license.
   * Called when user selects from dropdown (including "None" option).
   * @param licenseId - License ID to set, or null to clear
   */
  const setActive = async (licenseId: number | null): Promise<boolean> => {
    if (
      !marketplaceInfo?.branchMarketplaceId ||
      !marketplaceInfo?.marketplaceId
    ) {
      console.warn("Cannot set active license: marketplace info not available");
      return false;
    }

    try {
      await setActivePharmacyLicense(
        marketplaceInfo.branchMarketplaceId,
        marketplaceInfo.marketplaceId,
        licenseId
      );

      // Refresh detailed marketplace info in Redux to get updated license data
      await (dispatch as any)(
        fetchMarketplaceInfoDetailed(
          marketplaceInfo.branchMarketplaceId,
          marketplaceInfo.marketplaceId
        )
      );

      return true;
    } catch (err) {
      console.error("Failed to set active license:", err);
      setError("Failed to set active pharmacist");
      return false;
    }
  };

  return {
    licenses,
    loading,
    error,
    activeLicenseId,
    refetch: fetchLicenses,
    create,
    update,
    remove,
    setActive,
  };
};
